import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ActionButton, PageLayout } from '../../components/DesignSystem';
import { Button, Card, Input, Select, Space, message } from 'antd';
import { PlusOutlined, SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import API_URL from '../../config/api';
import { sendInvoiceEmail } from '../../utils/invoiceEmail';
import { apiFetch } from '../../utils/apiFetch';
import { showToast } from '../../utils/toast';
import { ListPagination } from '../../components/ListPagination';
import '../../styles/listPage.css';

type Invoice = {
  id: string;
  invoiceNumber?: string;
  invoice_number?: string;
  sourceOrderId?: number | string;
  source_order_id?: number | string;
  clientName: string;
  customer_id?: number;
  amount: number;
  dueDate: string;
  status: string;
  notes: string;
  description?: string;
  items?: Array<{ description?: string; [key: string]: any }>;
  createdAt: string;
  paid_amount?: number;
  remaining_balance?: number;
  days_overdue?: number;
};

type Payment = {
  id: number;
  amount: number;
  payment_date: string;
  payment_method?: string;
  notes?: string;
  recorded_by?: string;
  created_at?: string;
  recorded_by_name?: string;
  is_void?: boolean;
};

type AgingStats = {
  total: number;
  current: number;
  days_0_30: number;
  days_31_60: number;
  days_61_90: number;
  days_90_plus: number;
};

type CreditNote = {
  id: number;
  amount: number;
  remaining_balance: number;
  reason: string;
  issued_at: string;
};

/** Map legacy DB statuses to UI vocabulary */
const normalizeInvoiceStatus = (status: string) => {
  if (status === 'Partially Paid') return 'Partial';
  if (status === 'Pending') return 'Unpaid';
  return status;
};

const statusMatchesFilter = (invoiceStatus: string, filter: string) => {
  const s = normalizeInvoiceStatus(invoiceStatus);
  if (filter === 'Overdue') return s !== 'Paid'; // overdue handled separately via days_overdue
  if (filter === 'Partial') return s === 'Partial';
  if (filter === 'Unpaid') return s === 'Unpaid' || s === 'Draft';
  return s === filter;
};

const INVOICES_PAGE_SIZE = 10;

const csvCell = (value: unknown) => {
  const s = String(value ?? '');
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const InvoiceList: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [invoicePage, setInvoicePage] = useState(1);
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());
  const [customerName, setCustomerName] = useState<string | null>(null);
  const [customerDetails, setCustomerDetails] = useState<any>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState<Invoice | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentHistory, setPaymentHistory] = useState<Payment[]>([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedInvoiceForHistory, setSelectedInvoiceForHistory] = useState<any>(null);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [selectedInvoiceForCredit, setSelectedInvoiceForCredit] = useState<Invoice | null>(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [availableCredit, setAvailableCredit] = useState(0);
  const [activeCreditNotes, setActiveCreditNotes] = useState<CreditNote[]>([]);
  const [selectedCreditNoteId, setSelectedCreditNoteId] = useState<number | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const rawCustomerId = searchParams.get('customer_id');
  // Validate and parse customer ID - must be a valid number
  const customerId = rawCustomerId && rawCustomerId !== 'null' && rawCustomerId !== 'undefined' && !isNaN(Number(rawCustomerId)) 
    ? rawCustomerId 
    : null;

  useEffect(() => {
    fetchInvoices();
    // Reset filters when URL-based filtering is active
    if (customerId) {
      setSearchTerm('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate, customerId]);

  useEffect(() => {
    setInvoicePage(1);
  }, [searchTerm, statusFilter, customerId]);

  const fetchInvoices = async () => {
    try {
      setError(null);
      if (!token) {
        navigate('/login');
        return;
      }

      // Fetch customer name if filtering by customer
      if (customerId) {
        try {
          const customerRes = await apiFetch(`${API_URL}/customers/${customerId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (customerRes.ok) {
            const customer = await customerRes.json();
            setCustomerName(customer.name);
            setCustomerDetails(customer);
          } else {
            console.error(`❌ Customer ${customerId} not found (status ${customerRes.status})`);
            setCustomerName(null);
            setCustomerDetails(null);
          }
        } catch (e) {
          console.error('Failed to fetch customer name:', e);
          setCustomerName(null);
          setCustomerDetails(null);
        }
      } else {
        setCustomerName(null);
        setCustomerDetails(null);
      }

      const url = customerId
        ? `${API_URL}/invoices?customer_id=${customerId}`
        : `${API_URL}/invoices`;
      
      const res = await apiFetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();

      const invoicesArray = Array.isArray(data) ? data : [];

      // Fallback: if some invoices lack `clientName` but have customer_id, fetch those customers
      const missingCustomerIds = Array.from(new Set(
        invoicesArray
          .filter((inv: any) => (!inv.clientName || String(inv.clientName).trim() === '') && inv.customer_id)
          .map((inv: any) => String(inv.customer_id))
      ));

      if (missingCustomerIds.length > 0) {
        // limit to avoid huge parallel fetch storms
        const idsToFetch = missingCustomerIds.slice(0, 10);
        try {
          const fetched = await Promise.all(idsToFetch.map(id =>
            apiFetch(`${API_URL}/customers/${id}`, { headers: { 'Authorization': `Bearer ${token}` } })
              .then(r => r.ok ? r.json() : null)
              .catch(() => null)
          ));
          const customersById = new Map(fetched.filter(Boolean).map((c: any) => [String(c.id), c.name]));
          invoicesArray.forEach((inv: any) => {
            if ((!inv.clientName || String(inv.clientName).trim() === '') && inv.customer_id) {
              const name = customersById.get(String(inv.customer_id));
              if (name) inv.clientName = name;
            }
          });
        } catch (err) {
          console.warn('Failed to fetch missing customer names for invoices', err);
        }
      }

      // Normalize snake_case → camelCase so the rest of the list works unchanged
      const normalizedInvoices = invoicesArray.map((inv: any) => ({
        ...inv,
        clientName:    inv.clientName    || inv.client_name    || '',
        dueDate:       inv.dueDate       || inv.due_date       || '',
        createdAt:     inv.createdAt     || inv.created_at     || '',
        invoiceNumber: inv.invoiceNumber || inv.invoice_number || String(inv.id),
        customerAddress: inv.customerAddress || inv.customer_address || '',
        customerEmail: inv.customerEmail || inv.customer_email || '',
        customerPhone: inv.customerPhone || inv.customer_phone || '',
        invoiceDate:   inv.invoiceDate   || inv.invoice_date   || '',
        paidAmount:    inv.paidAmount    || inv.paid_amount    || 0,
        status: normalizeInvoiceStatus(inv.status || 'Unpaid'),
        sourceOrderId: inv.sourceOrderId || inv.source_order_id || null,
      }));
      setInvoices(normalizedInvoices);
    } catch (error) {
      console.error('❌ Error fetching invoices:', error);
      setError('Failed to load invoices');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };



  const handleSelectInvoice = (id: string, checked: boolean) => {
    const newSet = new Set(selectedInvoices);
    if (checked) {
      newSet.add(id);
    } else {
      newSet.delete(id);
    }
    setSelectedInvoices(newSet);
  };

  const handleBulkRemind = async () => {
    if (selectedInvoices.size === 0) {
      showToast('Please select invoices to send reminders');
      return;
    }

    if (!window.confirm(`Send payment reminders for ${selectedInvoices.size} invoice(s)?`)) {
      return;
    }

    try {
      const res = await apiFetch(`${API_URL}/invoices/bulk-remind`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ invoiceIds: Array.from(selectedInvoices) })
      });

      if (res.ok) {
        const result = await res.json();
        showToast(`✅ Sent ${result.sent} reminder(s). Failed: ${result.failed}`);
        setSelectedInvoices(new Set());
      } else {
        showToast('❌ Failed to send reminders');
      }
    } catch (error) {
      console.error('Error sending bulk reminders:', error);
      showToast('❌ Network error');
    }
  };

  const sendInvoice = async (id: string) => {
    const confirm = window.confirm('Send this invoice to the customer via email?');
    if (!confirm) return;
    try {
      await sendInvoiceEmail(id, token || '');
      showToast('✅ Invoice sent successfully');
    } catch (error) {
      console.error('Error sending invoice:', error);
      showToast(`❌ Failed to send invoice: ${(error as any)?.message || 'Unknown error'}`);
    }
  };

  const handleRecordPayment = async (e: any, invoice: Invoice) => {
    e.preventDefault();
    e.stopPropagation();
    
    setSelectedInvoiceForPayment(invoice);
    setPaymentAmount('');
    setPaymentMethod('Cash');
    setPaymentNotes('');
    setShowPaymentModal(true);
    
    // Fetch payment history
    try {
      const res = await apiFetch(`${API_URL}/invoice-payments/invoice/${invoice.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPaymentHistory(data);
      }
    } catch (error) {
      console.error('Error fetching payment history:', error);
    }
  };

  const handleViewHistory = async (e: React.MouseEvent, invoice: any) => {
    e.stopPropagation();
    setSelectedInvoiceForHistory(invoice);
    setShowHistoryModal(true);
    setPaymentHistory([]);
    
    try {
      const res = await apiFetch(`${API_URL}/invoice-payments/invoice/${invoice.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPaymentHistory(data);
      }
    } catch (error) {
      console.error('Error fetching payment history:', error);
    }
  };

  const handleSubmitPayment = async () => {
    if (!selectedInvoiceForPayment || submittingPayment) return;
    
    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) {
      showToast('Please enter a valid payment amount');
      return;
    }

    const remaining = Number(selectedInvoiceForPayment.amount || 0) - Number(selectedInvoiceForPayment.paid_amount || 0);
    if (amount > remaining) {
      showToast(`Payment amount (£${amount}) exceeds remaining balance (£${Number(remaining || 0).toFixed(2)})`);
      return;
    }

    setSubmittingPayment(true);
    try {
      const res = await apiFetch(`${API_URL}/invoice-payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          invoice_id: parseInt(selectedInvoiceForPayment.id),
          amount,
          payment_date: new Date().toISOString().split('T')[0],
          payment_method: paymentMethod,
          notes: paymentNotes
        })
      });

      if (res.ok) {
        message.success('✅ Payment recorded successfully');
        setShowPaymentModal(false);
        fetchInvoices(); // Refresh list
      } else {
        const error = await res.json();
        message.error(`Failed to record payment: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error recording payment:', error);
      message.error('Network error while recording payment');
    } finally {
      setSubmittingPayment(false);
    }
  };

  const handleApplyCredit = async (e: any, invoice: Invoice) => {
    e.preventDefault();
    e.stopPropagation();
    
    setSelectedInvoiceForCredit(invoice);
    setCreditAmount('');
    setSelectedCreditNoteId(null);
    setShowCreditModal(true);
    
    // Fetch customer credit balance
    if (!invoice.customer_id) {
      message.error('Customer ID not found for this invoice');
      return;
    }
    
    try {
      const res = await apiFetch(`${API_URL}/credit-notes/customer/${invoice.customer_id}/balance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAvailableCredit(data.credit_balance || 0);
        setActiveCreditNotes(data.active_credit_notes || []);
      } else {
        setAvailableCredit(0);
        setActiveCreditNotes([]);
      }
    } catch (error) {
      console.error('Error fetching credit balance:', error);
      setAvailableCredit(0);
      setActiveCreditNotes([]);
    }
  };

  const handleSubmitCredit = async () => {
    if (!selectedInvoiceForCredit || !selectedCreditNoteId) {
      showToast('Please select a credit note to apply');
      return;
    }
    
    const amount = parseFloat(creditAmount);
    if (!amount || amount <= 0) {
      showToast('Please enter a valid credit amount');
      return;
    }

    const remaining = Number(selectedInvoiceForCredit.amount || 0) - Number(selectedInvoiceForCredit.paid_amount || 0);
    if (amount > remaining) {
      showToast(`Credit amount (£${amount}) exceeds invoice remaining balance (£${Number(remaining || 0).toFixed(2)})`);
      return;
    }

    const selectedCredit = activeCreditNotes.find(cn => cn.id === selectedCreditNoteId);
    if (selectedCredit && amount > Number(selectedCredit.remaining_balance || 0)) {
      showToast(`Credit amount (£${amount}) exceeds available credit (£${Number(selectedCredit.remaining_balance || 0).toFixed(2)})`);
      return;
    }

    try {
      const res = await apiFetch(`${API_URL}/credit-notes/${selectedCreditNoteId}/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          invoice_id: parseInt(selectedInvoiceForCredit.id),
          amount
        })
      });

      if (res.ok) {
        message.success('✅ Credit applied successfully');
        setShowCreditModal(false);
        fetchInvoices(); // Refresh list
      } else {
        const error = await res.json();
        message.error(`Failed to apply credit: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error applying credit:', error);
      message.error('Network error while applying credit');
    }
  };

  // Calculate aging statistics
  const agingStats = invoices.reduce((acc, inv) => {
    const remaining = (inv.amount || 0) - (inv.paid_amount || 0);
    if (remaining > 0 && inv.status !== 'Paid') {
      acc.total += remaining;
      const days = inv.days_overdue || 0;
      if (days === 0) acc.current += remaining;
      else if (days <= 30) acc.days_0_30 += remaining;
      else if (days <= 60) acc.days_31_60 += remaining;
      else if (days <= 90) acc.days_61_90 += remaining;
      else acc.days_90_plus += remaining;
    }
    return acc;
  }, {
    total: 0,
    current: 0,
    days_0_30: 0,
    days_31_60: 0,
    days_61_90: 0,
    days_90_plus: 0
  });

  const filteredInvoices = invoices.filter(inv => {
    // Search filter (invoice #, customer name, description, amount)
    const searchLower = searchTerm.toLowerCase();
    const searchMatch = !searchTerm ||
      String(inv.id || '').toLowerCase().includes(searchLower) ||
      String(inv.invoiceNumber || '').toLowerCase().includes(searchLower) ||
      String(inv.clientName || '').toLowerCase().includes(searchLower) ||
      String(inv.notes || '').toLowerCase().includes(searchLower) ||
      String(inv.description || '').toLowerCase().includes(searchLower) ||
      String(inv.amount || '').includes(searchTerm);
    
    if (!searchMatch) return false;

    // Status filter
    if (statusFilter !== 'All') {
      if (statusFilter === 'Overdue' && (inv.status === 'Paid' || !inv.days_overdue || inv.days_overdue === 0)) {
        return false;
      } else if (statusFilter !== 'Overdue' && !statusMatchesFilter(inv.status, statusFilter)) {
        return false;
      }
    }

    return searchMatch;
  });

  const invoicePages = Math.max(1, Math.ceil(filteredInvoices.length / INVOICES_PAGE_SIZE));
  const safeInvoicePage = Math.min(invoicePage, invoicePages);
  const paginatedInvoices = filteredInvoices.slice(
    (safeInvoicePage - 1) * INVOICES_PAGE_SIZE,
    safeInvoicePage * INVOICES_PAGE_SIZE
  );

  const handleSelectPage = (checked: boolean) => {
    const newSet = new Set(selectedInvoices);
    paginatedInvoices.forEach(inv => {
      if (checked) newSet.add(inv.id);
      else newSet.delete(inv.id);
    });
    setSelectedInvoices(newSet);
  };

  const exportInvoicesCSV = () => {
    if (filteredInvoices.length === 0) {
      message.warning('No invoices to export');
      return;
    }
    const headers = ['Invoice #', 'Customer', 'Description', 'Amount', 'Paid', 'Remaining', 'Due Date', 'Status'];
    const rows = filteredInvoices.map(inv => {
      const remaining = inv.status === 'Paid'
        ? 0
        : Number((inv.remaining_balance ?? (inv.amount - (inv.paid_amount || 0))) || 0);
      const desc = inv.description || inv.notes ||
        (Array.isArray(inv.items) && inv.items[0]?.description) || '';
      return [
        inv.invoiceNumber || inv.id,
        inv.clientName || 'Guest',
        desc,
        Number(inv.amount || 0).toFixed(2),
        Number(inv.paid_amount || 0).toFixed(2),
        remaining.toFixed(2),
        inv.dueDate ? new Date(inv.dueDate).toLocaleDateString('en-GB') : '',
        inv.status || '',
      ].map(csvCell).join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoices-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    message.success(`Exported ${filteredInvoices.length} invoice${filteredInvoices.length !== 1 ? 's' : ''}`);
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'paid': return '#28a745';
      case 'unpaid': return '#ffc107';
      case 'overdue': return '#dc3545';
      default: return '#6c757d';
    }
  };

  if (loading) return <div style={styles.loading}>Loading invoices...</div>;

  if (error) {
    return (
      <PageLayout title="📄 Invoices" action={null}>
        <div style={styles.errorBox}>
          <h2>❌ Error Loading Invoices</h2>
          <p>{error}</p>
          <ActionButton variant="add" label="Retry" onClick={() => { setLoading(true); fetchInvoices(); }} />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      title="📄 Invoices" 
      action={
        <Button 
          type="primary" 
          size="large"
          icon={<PlusOutlined />} 
          onClick={() => navigate('/invoices/add')}
          style={{ borderRadius: '8px', height: '44px', fontWeight: 600 }}
        >
          Add Invoice
        </Button>
      }
    >
      {/* Customer Info Panel (shown when filtering by customer) */}
      {customerId && customerName && (
        <div style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #f8faff 100%)', border: '1.5px solid #c7d4f8', borderRadius: 12, padding: '20px 24px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', flex: 1 }}>
            <div>
              <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 4 }}>Customer</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#1e3a8a' }}>{customerDetails?.name || customerName}</div>
              {customerDetails?.company && <div style={{ fontSize: 13, color: '#6366f1', fontWeight: 600 }}>{customerDetails.company}</div>}
            </div>
            {customerDetails?.email && (
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 4 }}>Email</div>
                <div style={{ fontSize: 14, color: '#374151' }}>{customerDetails.email}</div>
              </div>
            )}
            {customerDetails?.phone && (
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 4 }}>Phone</div>
                <div style={{ fontSize: 14, color: '#374151' }}>{customerDetails.phone}</div>
              </div>
            )}
            {customerDetails?.address && (
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 4 }}>Address</div>
                <div style={{ fontSize: 14, color: '#374151', whiteSpace: 'pre-line' }}>{customerDetails.address}</div>
              </div>
            )}
            {customerDetails?.description && (
              <div style={{ flex: '1 1 100%' }}>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 4 }}>Notes</div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>{customerDetails.description}</div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <button onClick={() => navigate(`/customers/${customerId}`)} style={{ padding: '8px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>View Customer</button>
            <button style={styles.clearFilterButton} onClick={() => navigate('/invoices/list')}>✕ Show All</button>
          </div>
        </div>
      )}

      <Card>
      {/* Search and Filters */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <Space wrap>
          <Input
            placeholder="Search by invoice #, customer, description, or amount..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 350 }}
          />
          
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 150 }}
          >
            <Select.Option value="All">All Status</Select.Option>
            <Select.Option value="Draft">Draft</Select.Option>
            <Select.Option value="Unpaid">Unpaid</Select.Option>
            <Select.Option value="Partial">Partial</Select.Option>
            <Select.Option value="Paid">Paid</Select.Option>
            <Select.Option value="Overdue">Overdue</Select.Option>
          </Select>
        </Space>

        <Space>
          <Button icon={<DownloadOutlined />} onClick={exportInvoicesCSV}>
            Export CSV
          </Button>
          {(searchTerm || statusFilter !== 'All') && (
            <Button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('All');
              }}
            >
              Clear Filters
            </Button>
          )}
        </Space>
      </div>

      <ListPagination
        total={invoices.length}
        filtered={filteredInvoices.length}
        page={safeInvoicePage}
        pages={invoicePages}
        onPageChange={setInvoicePage}
        noun="invoices"
      />

      {/* Bulk Actions */}
      {selectedInvoices.size > 0 && (
        <div style={styles.bulkActions}>
          <span style={styles.bulkCount}>{selectedInvoices.size} selected</span>
          <ActionButton variant="add" label="Send Reminders" onClick={handleBulkRemind} />
          <button 
            style={styles.clearButton}
            onClick={() => setSelectedInvoices(new Set())}
          >
            Clear Selection
          </button>
        </div>
      )}

      {filteredInvoices.length === 0 ? (
        <div style={styles.emptyState}>
          <p>No invoices found matching your filters.</p>
          <ActionButton variant="add" label="Create Your First Invoice" onClick={() => navigate('/invoices/add')} />
        </div>
      ) : (
        <>
        <div className="list-table-panel">
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeader}>
                <th style={{ ...styles.th, width: '3%' }}>
                  <input 
                    type="checkbox"
                    checked={paginatedInvoices.length > 0 && paginatedInvoices.every(inv => selectedInvoices.has(inv.id))}
                    onChange={e => handleSelectPage(e.target.checked)}
                  />
                </th>
                <th style={{ ...styles.th, width: '9%' }}>Invoice #</th>
                <th style={{ ...styles.th, width: '11%' }}>Customer</th>
                <th style={{ ...styles.th, width: '16%' }}>Description</th>
                <th style={{ ...styles.th, width: '8%' }}>Amount</th>
                <th style={{ ...styles.th, width: '8%' }}>Paid</th>
                <th style={{ ...styles.th, width: '8%' }}>Remaining</th>
                <th style={{ ...styles.th, width: '9%' }}>Due Date</th>
                <th style={{ ...styles.th, width: '8%' }}>Status</th>
                <th style={{ ...styles.th, width: '20%' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedInvoices.map(invoice => {
                const remaining = (invoice.amount || 0) - (invoice.paid_amount || 0);
                const daysOverdue = invoice.days_overdue || 0;
                
                return (
                  <tr key={invoice.id} style={styles.tableRow}>
                    <td style={styles.td}>
                      <input 
                        type="checkbox"
                        checked={selectedInvoices.has(invoice.id)}
                        onChange={e => handleSelectInvoice(invoice.id, e.target.checked)}
                      />
                    </td>
                    <td style={styles.td}>
                      <strong>#{invoice.invoiceNumber || invoice.id}</strong>
                      {invoice.sourceOrderId && (
                        <div
                          style={{ fontSize: 11, color: '#6366f1', cursor: 'pointer', marginTop: 2 }}
                          onClick={() => navigate(`/orders/view/${invoice.sourceOrderId}`)}
                        >
                          📦 Order #{invoice.sourceOrderId}
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>
                      {invoice.clientName || <span style={{color: '#999', fontStyle: 'italic'}}>Guest</span>}
                    </td>
                    <td style={{ ...styles.td, overflow: 'hidden' }}>
                      <div style={styles.description}>
                        {invoice.description || invoice.notes ||
                          (Array.isArray(invoice.items) && invoice.items[0]?.description) ||
                          '-'}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <strong>£{Number(invoice.amount || 0).toFixed(2)}</strong>
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span>£{Number(invoice.paid_amount || 0).toFixed(2)}</span>
                        {invoice.paid_amount > 0 && (
                          <button 
                            onClick={(e) => handleViewHistory(e, invoice)}
                            style={{ 
                              background: 'none', 
                              border: 'none', 
                              color: '#007bff', 
                              fontSize: '11px', 
                              cursor: 'pointer', 
                              padding: 0, 
                              textAlign: 'left',
                              textDecoration: 'underline'
                            }}
                          >
                            📜 View History
                          </button>
                        )}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <strong style={{ color: (invoice.status === 'Paid' ? '#28a745' : (remaining > 0 ? '#dc3545' : '#28a745')) }}>
                        {invoice.status === 'Paid' ? '£0.00' : `£${Number((invoice.remaining_balance ?? (invoice.amount - (invoice.paid_amount || 0))) || 0).toFixed(2)}`}
                      </strong>
                    </td>
                    <td style={styles.td}>
                      <div>
                        {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('en-GB') : '-'}
                      </div>
                      {daysOverdue > 0 && invoice.status !== 'Paid' && (
                        <div style={{
                          fontSize: 11,
                          color: daysOverdue > 30 ? '#dc3545' : '#ffc107',
                          fontWeight: 600,
                          marginTop: 2
                        }}>
                          {daysOverdue}d overdue
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.statusBadge,
                        backgroundColor: getStatusColor(invoice.status)
                      }}>
                        {invoice.status || 'Unknown'}
                      </span>
                    </td>
                    <td style={{ ...styles.td, overflow: 'visible' }}>
                      <div style={styles.actionButtons}>
                        {invoice.status !== 'Paid' && (
                          <>
                            <button 
                              style={styles.payButton}
                              onClick={(e) => handleRecordPayment(e, invoice)}
                              title="Record Payment"
                            >
                              💰 Pay
                            </button>
                            {invoice.customer_id && (
                              <button 
                                style={{...styles.payButton, background: 'linear-gradient(135deg, #00942a 0%, #01470a 100%)'}}
                                onClick={(e) => handleApplyCredit(e, invoice)}
                                title="Apply Credit"
                              >
                                🎫 Credit
                              </button>
                            )}
                          </>
                        )}
                        <ActionButton 
                          variant="view" 
                          onClick={() => navigate(`/invoices/view/${invoice.id}`)} 
                        />
                        <ActionButton
                          variant="add"
                          label="Send"
                          onClick={() => sendInvoice(invoice.id)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </div>

        <div className="list-mobile-cards">
          {paginatedInvoices.map(invoice => {
            const remaining = (invoice.amount || 0) - (invoice.paid_amount || 0);
            const daysOverdue = invoice.days_overdue || 0;
            return (
              <div key={invoice.id} className="list-mobile-card">
                <div className="list-mobile-card-header">
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="list-mobile-card-title">#{invoice.invoiceNumber || invoice.id}</div>
                    <div className="list-mobile-card-meta">{invoice.clientName || 'Guest'}</div>
                  </div>
                  <span style={{ ...styles.statusBadge, backgroundColor: getStatusColor(invoice.status) }}>
                    {invoice.status || 'Unknown'}
                  </span>
                </div>
                <div className="list-mobile-card-info">
                  <div><div className="list-mobile-card-label">Amount</div>£{Number(invoice.amount || 0).toFixed(2)}</div>
                  <div><div className="list-mobile-card-label">Remaining</div>£{remaining.toFixed(2)}</div>
                  <div><div className="list-mobile-card-label">Due</div>{invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('en-GB') : '—'}</div>
                  <div><div className="list-mobile-card-label">Overdue</div>{daysOverdue > 0 ? `${daysOverdue}d` : '—'}</div>
                </div>
                <div className="list-mobile-actions">
                  <div style={styles.actionButtons}>
                    {invoice.status !== 'Paid' && (
                      <>
                        <button style={styles.payButton} onClick={(e) => handleRecordPayment(e, invoice)} title="Record Payment">💰 Pay</button>
                        {invoice.customer_id && (
                          <button style={{...styles.payButton, background: 'linear-gradient(135deg, #00942a 0%, #01470a 100%)'}} onClick={(e) => handleApplyCredit(e, invoice)} title="Apply Credit">🎫 Credit</button>
                        )}
                      </>
                    )}
                    <ActionButton variant="view" onClick={() => navigate(`/invoices/view/${invoice.id}`)} />
                    <ActionButton variant="add" label="Send" onClick={() => sendInvoice(invoice.id)} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}
      </Card>

      {/* Summary Stats */}
      {invoices.length > 0 && (
        <div style={styles.stats}>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Total Invoices</div>
            <div style={styles.statValue}>{invoices.length}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Paid Invoices</div>
            <div style={styles.statValue}>
              {invoices.filter(inv => inv.status === 'Paid').length}
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Total Outstanding</div>
            <div style={{...styles.statValue, color: '#dc3545'}}>
              £{Number(agingStats.total || 0).toFixed(2)}
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Overdue Invoices</div>
            <div style={{...styles.statValue, color: '#FF4136'}}>
              {invoices.filter(inv => inv.status !== 'Paid' && (inv.days_overdue || 0) > 0).length}
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedInvoiceForPayment && (
        <div style={styles.modalOverlay} onClick={() => setShowPaymentModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Record Payment</h2>
            <div style={styles.modalBody}>
              <p><strong>Invoice:</strong> #{selectedInvoiceForPayment.id}</p>
              <p><strong>Client:</strong> {selectedInvoiceForPayment.clientName}</p>
              <p><strong>Total Amount:</strong> £{Number(selectedInvoiceForPayment.amount || 0).toFixed(2)}</p>
              <p><strong>Paid:</strong> £{Number(selectedInvoiceForPayment.paid_amount || 0).toFixed(2)}</p>
              <p><strong>Remaining:</strong> £{(Number(selectedInvoiceForPayment.amount || 0) - Number(selectedInvoiceForPayment.paid_amount || 0)).toFixed(2)}</p>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Payment Amount (£)</label>
                <input 
                  type="number"
                  step="0.01"
                  value={paymentAmount}
                  onChange={e => setPaymentAmount(e.target.value)}
                  style={styles.input}
                  placeholder="0.00"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Payment Method</label>
                <select 
                  value={paymentMethod}
                  onChange={e => setPaymentMethod(e.target.value)}
                  style={styles.input}
                >
                  <option value="Cash">Cash</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Card">Card</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Notes</label>
                <textarea 
                  value={paymentNotes}
                  onChange={e => setPaymentNotes(e.target.value)}
                  style={styles.textarea}
                  placeholder="Optional notes..."
                />
              </div>

              {paymentHistory.length > 0 && (
                <div style={styles.paymentHistory}>
                  <h3 style={styles.historyTitle}>📜 Payment Activity (Steps)</h3>
                  {paymentHistory.map((payment, idx) => (
                    <div key={idx} style={{...styles.historyItem, flexDirection: 'column', alignItems: 'flex-start', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '10px'}}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontWeight: 'bold' }}>
                        <span>£{Number(payment.amount || 0).toFixed(2)}</span>
                        <span>{payment.payment_method}</span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        📅 Date: {new Date(payment.created_at || payment.payment_date).toLocaleString('en-GB', { 
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit' 
                        })}
                      </div>
                      {payment.notes && (
                        <div style={{ fontSize: '12px', fontStyle: 'italic', color: '#888', marginTop: '4px' }}>
                          📝 Notes: {payment.notes}
                        </div>
                      )}
                      {payment.recorded_by_name && (
                        <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>
                          👤 Recorded by: {payment.recorded_by_name}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={styles.modalActions}>
              <button style={{...styles.submitButton, opacity: submittingPayment ? 0.6 : 1}} onClick={handleSubmitPayment} disabled={submittingPayment}>
                {submittingPayment ? '⏳ Recording…' : 'Record Payment'}
              </button>
              <button style={styles.cancelButton} onClick={() => setShowPaymentModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment History Modal */}
      {showHistoryModal && selectedInvoiceForHistory && (
        <div style={styles.modalOverlay} onClick={() => setShowHistoryModal(false)}>
          <div style={{...styles.modalContent, maxWidth: '500px'}} onClick={e => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Payment History</h2>
            <div style={styles.modalBody}>
              <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', borderLeft: '4px solid #007bff' }}>
                <p><strong>Invoice:</strong> #{selectedInvoiceForHistory.id}</p>
                <p><strong>Customer:</strong> {selectedInvoiceForHistory.clientName}</p>
                <p><strong>Total Paid:</strong> £{Number(selectedInvoiceForHistory.paid_amount || 0).toFixed(2)} / £{Number(selectedInvoiceForHistory.amount || 0).toFixed(2)}</p>
              </div>

              {(() => {
                const sumNonVoided = paymentHistory.filter(p => !p.is_void).reduce((s, p) => s + Number(p.amount || 0), 0);
                const depositAmt = Math.max(0, Number(selectedInvoiceForHistory.paid_amount || 0) - sumNonVoided);
                return (
                  <>
                    {depositAmt > 0.005 && (
                      <div style={{ padding: '12px', borderBottom: '1px solid rgb(0,0,0)', display: 'flex', flexDirection: 'column', gap: '4px', backgroundColor: '#e6f7e6' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 'bold', fontSize: '16px', color: '#28a745' }}>
                            £{depositAmt.toFixed(2)}
                          </span>
                          <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', backgroundColor: '#d4edda', color: '#155724' }}>
                            Order Deposit
                          </span>
                        </div>
                        <div style={{ fontSize: '13px', color: '#555' }}>💼 Deposit confirmed before invoice</div>
                        <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>👤 Recorded by System</div>
                      </div>
                    )}
                  </>
                );
              })()}
              {paymentHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                  No additional payment records found for this invoice.
                </div>
              ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {paymentHistory.map((payment, idx) => (
                    <div key={idx} style={{ 
                      padding: '12px', 
                      borderBottom: '1px solid rgb(0, 0, 0)', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '4px',
                      backgroundColor: payment.is_void ? 'rgb(254, 246, 1)' : 'transparent'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '16px', color: payment.is_void ? '#cf1322' : '#28a745' }}>
                          £{Number(payment.amount || 0).toFixed(2)}
                          {payment.is_void && ' (VOIDED)'}
                        </span>
                        <span style={{ 
                          padding: '2px 8px', 
                          borderRadius: '12px', 
                          fontSize: '11px', 
                          backgroundColor: '#e6f7ff', 
                          color: '#0050b3' 
                        }}>
                          {payment.payment_method}
                        </span>
                      </div>
                      
                      <div style={{ fontSize: '13px', color: '#555' }}>
                        📅 {new Date(payment.created_at || payment.payment_date).toLocaleString('en-GB', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </div>

                      {payment.notes && (
                        <div style={{ fontSize: '12px', fontStyle: 'italic', color: '#888' }}>
                          📝 {payment.notes}
                        </div>
                      )}

                      <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                        👤 Recorded by {payment.recorded_by_name || 'System'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={styles.modalActions}>
              <button style={styles.submitButton} onClick={() => setShowHistoryModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credit Application Modal */}
      {showCreditModal && selectedInvoiceForCredit && (
        <div style={styles.modalOverlay} onClick={() => setShowCreditModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Apply Credit to Invoice</h2>
            <div style={styles.modalBody}>
              <p><strong>Invoice:</strong> #{selectedInvoiceForCredit.id}</p>
              <p><strong>Client:</strong> {selectedInvoiceForCredit.clientName}</p>
              <p><strong>Total Amount:</strong> £{Number(selectedInvoiceForCredit.amount || 0).toFixed(2)}</p>
              <p><strong>Paid:</strong> £{Number(selectedInvoiceForCredit.paid_amount || 0).toFixed(2)}</p>
              <p><strong>Remaining:</strong> £{(Number(selectedInvoiceForCredit.amount || 0) - Number(selectedInvoiceForCredit.paid_amount || 0)).toFixed(2)}</p>
              
              {availableCredit > 0 ? (
                <>
                  <div style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '14px', opacity: 0.9 }}>Available Credit Balance</div>
                    <div style={{ fontSize: '20px', fontWeight: 'bold' }}>£{Number(availableCredit || 0).toFixed(2)}</div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Select Credit Note</label>
                    <select 
                      value={selectedCreditNoteId || ''}
                      onChange={e => {
                        const noteId = parseInt(e.target.value);
                        setSelectedCreditNoteId(noteId);
                        const note = activeCreditNotes.find(cn => cn.id === noteId);
                        if (note) {
                          const maxApply = Math.min(
                            Number(note.remaining_balance || 0),
                            Number(selectedInvoiceForCredit.amount || 0) - Number(selectedInvoiceForCredit.paid_amount || 0)
                          );
                          setCreditAmount(maxApply.toFixed(2));
                        }
                      }}
                      style={styles.input}
                    >
                      <option value="">-- Select Credit Note --</option>
                      {activeCreditNotes.map(cn => (
                        <option key={cn.id} value={cn.id}>
                          #{cn.id} - £{Number(cn.remaining_balance || 0).toFixed(2)} ({cn.reason}) - {new Date(cn.issued_at).toLocaleDateString('en-GB')}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Credit Amount to Apply (£)</label>
                    <input 
                      type="number"
                      step="0.01"
                      value={creditAmount}
                      onChange={e => setCreditAmount(e.target.value)}
                      style={styles.input}
                      placeholder="0.00"
                    />
                  </div>
                </>
              ) : (
                <div style={{
                  background: '#fee2e2',
                  color: '#991b1b',
                  padding: '12px',
                  borderRadius: '8px',
                  marginTop: '16px',
                  textAlign: 'center'
                }}>
                  ℹ️ Customer has no available credit balance
                </div>
              )}
            </div>

            <div style={styles.modalActions}>
              <button 
                style={styles.submitButton} 
                onClick={handleSubmitCredit}
                disabled={!selectedCreditNoteId || !creditAmount || availableCredit === 0}
              >
                Apply Credit
              </button>
              <button style={styles.cancelButton} onClick={() => setShowCreditModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

const InvoiceList_useResponsive = () => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

const styles: { [key: string]: React.CSSProperties } = {
  filterBar: {
    display: 'flex',
    gap: 12,
    marginBottom: 20,
    flexWrap: 'wrap',
    alignItems: 'center',
    background: '#fff',
    padding: 20,
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  searchInput: {
    flex: '1 1 300px',
    padding: '12px 16px',
    border: '2px solid #e0e0e0',
    borderRadius: 8,
    fontSize: 14,
    outline: 'none',
    transition: 'border-color 0.2s',
    fontFamily: 'Arial, sans-serif'
  },
  filterSelect: {
    padding: '12px 16px',
    border: '2px solid #e0e0e0',
    borderRadius: 8,
    fontSize: 14,
    outline: 'none',
    cursor: 'pointer',
    background: '#fff',
    transition: 'border-color 0.2s',
    fontFamily: 'Arial, sans-serif',
    minWidth: 150
  },
  clearButton: {
    padding: '12px 20px',
    background: '#FF4136',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    transition: 'background 0.2s'
  },
  resultsInfo: {
    fontSize: 14,
    color: '#6c757d',
    marginBottom: 15,
    fontWeight: 500
  },
  filterBanner: {
    background: '#e3f2fd',
    padding: '15px 20px',
    borderRadius: 8,
    marginBottom: 20,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: '1px solid #90caf9'
  },
  filterText: {
    color: '#1976d2',
    fontSize: 14,
    fontWeight: 500
  },
  clearFilterButton: {
    padding: '6px 12px',
    background: '#1976d2',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600
  },
  description: {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: 13,
    color: '#666'
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 20,
    marginTop: 30
  },
  statCard: {
    flex: 1,
    background: '#fff',
    padding: 20,
    borderRadius: 10,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    textAlign: 'center'
  },
  statLabel: {
    fontSize: 13,
    color: '#6c757d',
    marginBottom: 8,
    fontWeight: 600
  },
  statValue: {
    fontSize: 24,
    fontWeight: 700,
    color: '#333'
  },
  bulkActions: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    marginBottom: 15,
    padding: '12px 15px',
    backgroundColor: '#e3f2fd',
    borderRadius: 8,
    border: '1px solid #90caf9'
  },
  bulkCount: {
    fontWeight: 600,
    color: '#1976d2',
    marginRight: 'auto'
  },
  loading: {
    textAlign: 'center',
    padding: 50,
    fontSize: 16,
    color: '#666'
  },
  emptyState: {
    textAlign: 'center',
    padding: 60,
    backgroundColor: '#f8f9fa',
    borderRadius: 12,
    border: '2px dashed #ddd'
  },
  tableWrapper: {
    backgroundColor: '#fff',
    borderRadius: 2,
    border: '1px solid #f0f0f0',
    overflow: 'hidden',
    marginTop: 16
  },
  table: {
    width: '100%',
    tableLayout: 'fixed',
    borderCollapse: 'collapse'
  },
  tableHeader: {
    backgroundColor: '#fafafa',
    borderBottom: '1px solid #f0f0f0'
  },
  th: {
    padding: '10px 8px',
    textAlign: 'left',
    fontWeight: 500,
    fontSize: 12,
    color: 'rgba(0, 0, 0, 0.85)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  tableRow: {
    borderBottom: '1px solid #f0f0f0',
    transition: 'background 0.2s'
  },
  td: {
    padding: '10px 8px',
    fontSize: 13,
    color: 'rgba(0, 0, 0, 0.85)',
    verticalAlign: 'middle',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  statusBadge: {
    display: 'inline-block',
    padding: '5px 12px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    color: '#fff',
    textTransform: 'capitalize'
  },
  agingBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    color: '#fff'
  },
  actionButtons: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  payButton: {
    padding: '6px 10px',
    borderRadius: 6,
    border: '1px solid #28a745',
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontSize: 16,
    transition: 'all 0.2s'
  },
  errorBox: {
    background: '#fff',
    padding: 40,
    borderRadius: 8,
    textAlign: 'center',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    marginTop: 50
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 30,
    maxWidth: 500,
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto'
  },
  modalTitle: {
    margin: '0 0 20px 0',
    fontSize: 24,
    fontWeight: 600,
    color: '#333'
  },
  modalBody: {
    marginBottom: 20
  },
  formGroup: {
    marginBottom: 15
  },
  label: {
    display: 'block',
    marginBottom: 5,
    fontSize: 14,
    fontWeight: 600,
    color: '#555'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 6,
    border: '1px solid #ddd',
    fontSize: 14,
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 6,
    border: '1px solid #ddd',
    fontSize: 14,
    minHeight: 80,
    boxSizing: 'border-box',
    fontFamily: 'inherit'
  },
  paymentHistory: {
    marginTop: 20,
    padding: 15,
    backgroundColor: '#f8f9fa',
    borderRadius: 8
  },
  historyTitle: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 10,
    color: '#333'
  },
  historyItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid #e9ecef',
    fontSize: 13
  },
  modalActions: {
    display: 'flex',
    gap: 10,
    justifyContent: 'flex-end'
  },
  submitButton: {
    padding: '10px 20px',
    borderRadius: 6,
    border: 'none',
    backgroundColor: '#007bff',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer'
  },
  cancelButton: {
    padding: '10px 20px',
    borderRadius: 6,
    border: '1px solid #ddd',
    backgroundColor: '#fff',
    color: '#333',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer'
  }
};

export default InvoiceList;